<script setup>
  import { computed, onMounted, ref, useId, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import ChartCard from './ChartCard.vue';
  import TimeSeriesChart from './TimeSeriesChart.vue';
  import DonutChart from './DonutChart.vue';
  import TopCarouselsChart from './TopCarouselsChart.vue';
  import SectionToggle from './SectionToggle.vue';
  import PreviousPeriodFigure from './PreviousPeriodFigure.vue';
  import Loader from '../ui/Loader.vue';
  import useCollapsedSections from '../../composables/useCollapsedSections';
  import { formatPercent } from '../../utils/format_percent';

  const { t } = useI18n();
  const i18nScope = 'components.sales_dashboard';

  const props = defineProps({
    granularities: { type: Array, required: true },
    defaultGranularity: { type: String, required: true },
    maxBuckets: { type: Number, required: true },
    // The three lists are already limited to what the signed-in user may
    // report on, so a filter can only ever narrow their own scope.
    // [{ id: '2', name: 'LesLibraires.ca' }]
    networks: { type: Array, default: () => [] },
    // [{ id: 'FRA', name: 'France' }], a Territory id and its name
    countries: { type: Array, default: () => [] },
    // [{ id: 7, name: 'Hogwarts', networkId: '2', countryId: 'FRA' }]
    libraries: { type: Array, default: () => [] },
    // ['cantook', 'pnb'], every active provider, for an internal user only:
    // empty for anyone else, which hides the filter. The provider narrows the
    // figures within the library scope, so it cannot widen it either.
    providers: { type: Array, default: () => [] },
    dataUrl: { type: String, required: true },
    exportUrl: { type: String, required: true }
  });

  const loading = ref(true);
  const error = ref(null);
  const dashboard = ref(null);

  const startDate = ref(defaultStartDate());
  const endDate = ref(today());
  const granularity = ref(props.defaultGranularity);
  const networkId = ref('');
  const countryId = ref('');
  const libraryId = ref('');
  const provider = ref('');

  // A single option is not a choice: with one network, one country, one
  // library or one provider there is nothing to narrow to.
  const canFilterByNetwork = computed(() => props.networks.length > 1);
  const canFilterByCountry = computed(() => props.countries.length > 1);
  const canFilterByLibrary = computed(() => props.libraries.length > 1);
  const canFilterByProvider = computed(() => props.providers.length > 1);

  // Picking a network or a country narrows the library list to the libraries
  // that match both.
  const availableLibraries = computed(() =>
    props.libraries.filter(
      (library) =>
        (!networkId.value || library.networkId === networkId.value) &&
        (!countryId.value || library.countryId === countryId.value)
    )
  );

  // Drop a library selection the new network or country no longer contains,
  // otherwise the filters would intersect to nothing while all still look
  // valid.
  watch([networkId, countryId], () => {
    if (!libraryId.value) return;
    if (!availableLibraries.value.some((library) => library.id === libraryId.value)) libraryId.value = '';
  });

  // The filters the figures on screen were loaded with. The selects can move
  // on before Refresh is pressed, and a request can fail, so the scope notice
  // and the CSV export read these rather than the live values, and always
  // describe the figures shown.
  const applied = ref({ networkId: '', countryId: '', libraryId: '', provider: '', query: '' });

  const selectedNetwork = computed(() => props.networks.find((network) => network.id === applied.value.networkId));
  const selectedCountry = computed(() => props.countries.find((country) => country.id === applied.value.countryId));
  const selectedLibrary = computed(() => props.libraries.find((library) => library.id === applied.value.libraryId));

  const libraryScopeNotice = computed(() => {
    if (selectedLibrary.value) {
      return t(`${i18nScope}.scope_notice_library`, { library: selectedLibrary.value.name });
    }
    if (selectedNetwork.value && selectedCountry.value) {
      return t(`${i18nScope}.scope_notice_network_country`, {
        network: selectedNetwork.value.name,
        country: selectedCountry.value.name
      });
    }
    if (selectedNetwork.value) {
      return t(`${i18nScope}.scope_notice_network`, { network: selectedNetwork.value.name });
    }
    if (selectedCountry.value) {
      return t(`${i18nScope}.scope_notice_country`, { country: selectedCountry.value.name });
    }
    return t(`${i18nScope}.scope_notice_all`);
  });

  // The provider narrows what is counted rather than whose libraries, so it
  // reads as a sentence of its own after the libraries.
  const scopeNotice = computed(() => {
    if (!applied.value.provider) return libraryScopeNotice.value;

    return `${libraryScopeNotice.value} ${t(`${i18nScope}.scope_notice_provider`, { provider: applied.value.provider })}`;
  });

  function today() {
    return new Date().toISOString().split('T')[0];
  }

  function defaultStartDate() {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  }

  const queryParams = computed(() => {
    const params = new URLSearchParams({
      start_date: startDate.value,
      end_date: endDate.value,
      granularity: granularity.value
    });
    if (networkId.value) params.append('network_id', networkId.value);
    if (countryId.value) params.append('country', countryId.value);
    if (libraryId.value) params.append('library_id', libraryId.value);
    if (provider.value) params.append('provider', provider.value);
    return params;
  });

  // Mirrors the server-side bucket cap so an unusable combination is caught
  // before a request goes out. The server still enforces it.
  const projectedBuckets = computed(() => {
    const start = new Date(`${startDate.value}T00:00:00Z`);
    const end = new Date(`${endDate.value}T00:00:00Z`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;

    const days = (end - start) / 86400000 + 1;
    if (granularity.value === 'day') return Math.ceil(days);
    if (granularity.value === 'week') return Math.ceil(days / 7) + 1;
    if (granularity.value === 'year') return Math.ceil(days / 365) + 1;
    return Math.ceil(days / 28) + 1;
  });

  const rangeInverted = computed(() => new Date(endDate.value) < new Date(startDate.value));
  const tooManyBuckets = computed(() => projectedBuckets.value > props.maxBuckets);
  const canQuery = computed(() => !rangeInverted.value && !tooManyBuckets.value);

  // Bumped on every request, so a slower, superseded response cannot land
  // after the latest one and show figures the filters no longer describe.
  let latestRequest = 0;

  async function fetchData() {
    if (!canQuery.value) return;

    const request = ++latestRequest;
    loading.value = true;
    error.value = null;
    const requested = {
      networkId: networkId.value,
      countryId: countryId.value,
      libraryId: libraryId.value,
      provider: provider.value,
      query: queryParams.value.toString()
    };

    try {
      const response = await fetch(`${props.dataUrl}?${requested.query}`, {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      });
      const payload = await response.json();
      if (request !== latestRequest) return;

      if (!response.ok) {
        error.value = payload.error || t(`${i18nScope}.error_loading`);
        dashboard.value = null;
        return;
      }

      dashboard.value = payload;
      applied.value = requested;
    } catch (exception) {
      if (request !== latestRequest) return;

      console.error('Error loading sales dashboard:', exception);
      error.value = t(`${i18nScope}.error_loading`);
      dashboard.value = null;
    } finally {
      if (request === latestRequest) loading.value = false;
    }
  }

  const exportHref = computed(() => `${props.exportUrl}?${applied.value.query}`);

  // --- formatting ---------------------------------------------------------

  function formatCount(value) {
    return new Intl.NumberFormat().format(value);
  }

  function formatAmount(value) {
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  }

  // A bucket at the granularity on screen, or a plain date at the 'day' one.
  function formatBucket(bucket, unit = granularity.value) {
    const date = new Date(`${bucket}T00:00:00Z`);
    const options = { timeZone: 'UTC' };

    if (unit === 'year') return date.toLocaleDateString(undefined, { ...options, year: 'numeric' });
    if (unit === 'month') {
      return date.toLocaleDateString(undefined, { ...options, year: 'numeric', month: 'short' });
    }
    return date.toLocaleDateString(undefined, { ...options, year: 'numeric', month: 'short', day: 'numeric' });
  }

  function summaryFor(chartKey, total, series) {
    if (!series || series.length === 0) return null;

    return t(`${i18nScope}.chart_summary`, {
      chart: t(`${i18nScope}.charts.${chartKey}`),
      total,
      periods: series.length,
      start: formatBucket(series[0].bucket),
      end: formatBucket(series[series.length - 1].bucket)
    });
  }

  // A count's share of a total, one decimal place, for the breakdown tables.
  function formatShare(count, total) {
    return formatPercent(total === 0 ? 0 : count / total);
  }

  function sumCounts(entries) {
    return entries.reduce((sum, entry) => sum + entry.count, 0);
  }

  // The text alternative to a breakdown chart: each entry's label and count.
  function breakdownSummary(entries) {
    if (entries.length === 0) return t(`${i18nScope}.no_data`);

    return entries.map((entry) => `${entry.label}: ${formatCount(entry.count)}`).join('. ');
  }

  function seriesRows(series, formatter) {
    return series.map((point) => [formatBucket(point.bucket), formatter(point.value)]);
  }

  // One row per period, one column per series, for a chart carrying several.
  function multiSeriesRows(series, formatter) {
    if (series.length === 0) return [];

    return series[0].points.map((point, index) => [
      formatBucket(point.bucket),
      ...series.map((entry) => formatter(entry.points[index]?.value ?? 0))
    ]);
  }

  // --- derived views of the payload ---------------------------------------

  const totals = computed(() => dashboard.value?.totals);

  // The same series over the run of periods just before the range, one point
  // per point of the range, so a chart can draw a series beside its earlier
  // self. Always present in the payload, even when there was no activity.
  const previousPeriod = computed(() => dashboard.value?.previous_period);

  // A currency's revenue entry over the previous period. A currency with no
  // sales back then is absent from the payload, and reads as zero throughout,
  // split included, rather than as no comparison to offer.
  function previousRevenue(currency) {
    const previous = previousPeriod.value;
    const entry = previous.revenue.find((candidate) => candidate.currency === currency);
    if (entry) return entry;

    const zeros = previous.buckets.map((bucket) => ({ bucket, value: 0 }));
    return { series: zeros, from_scheduled_cart: { series: zeros }, not_from_scheduled_cart: { series: zeros } };
  }

  // A currency's revenue chart carries three series on the one amount axis:
  // the revenue, and the same split the sales chart makes of its count -- the
  // part paid on carts an automated cart created, and the rest. The two
  // partition the revenue, and each declares `shareOf: 'revenue'` so a
  // period's readout carries its share of that period's revenue.
  function revenueSeries(entry) {
    const previous = previousRevenue(entry.currency);

    return [
      {
        key: 'revenue',
        label: t(`${i18nScope}.revenue_in`, { currency: entry.currency.toUpperCase() }),
        points: entry.series,
        previousPoints: previous.series
      },
      {
        key: 'from_scheduled_cart',
        label: t(`${i18nScope}.charts.from_scheduled_cart`),
        points: entry.from_scheduled_cart.series,
        previousPoints: previous.from_scheduled_cart.series,
        shareOf: 'revenue'
      },
      {
        key: 'not_from_scheduled_cart',
        label: t(`${i18nScope}.charts.not_from_scheduled_cart`),
        points: entry.not_from_scheduled_cart.series,
        previousPoints: previous.not_from_scheduled_cart.series,
        shareOf: 'revenue'
      }
    ];
  }

  function revenueTableColumns(entry) {
    return [t(`${i18nScope}.period`), ...revenueSeries(entry).map((series) => series.label)];
  }

  function revenueSummary(entry) {
    const currency = entry.currency.toUpperCase();
    const revenue = summaryFor('revenue', `${formatAmount(entry.total)} ${currency}`, entry.series);
    const share = entry.total === 0 ? 0 : entry.from_scheduled_cart.total / entry.total;
    const fromScheduledCart = t(`${i18nScope}.revenue_from_scheduled_cart_summary`, {
      total: `${formatAmount(entry.from_scheduled_cart.total)} ${currency}`,
      percent: formatPercent(share)
    });

    return `${revenue} ${fromScheduledCart}`;
  }

  // The sales chart carries five series on the one count axis: the sales, and
  // four subsets of them -- the part that is a library buying a book it already
  // had, the part bought out of the back catalog, and the sales split by
  // whether the copy was bought on a cart a scheduled cart created. The first
  // two overlap rather than partition the sales; the last two do partition
  // them, but are drawn beside the total like the others rather than stacked,
  // so every line reads on the same axis. All four declare `shareOf: 'sales'`,
  // so a period's readout carries the share of that period's sales each one
  // makes up.
  //
  // Each series also carries its previous-period points, which the chart
  // offers to draw beside it.
  const salesSeries = computed(() => {
    if (!dashboard.value) return [];
    const previous = previousPeriod.value;

    return [
      {
        key: 'sales',
        label: t(`${i18nScope}.charts.sales_count`),
        points: dashboard.value.sales,
        previousPoints: previous.sales
      },
      {
        key: 'repurchases',
        label: t(`${i18nScope}.charts.repurchases`),
        points: dashboard.value.repurchases,
        previousPoints: previous.repurchases,
        shareOf: 'sales'
      },
      {
        key: 'back_catalog',
        label: t(`${i18nScope}.charts.back_catalog`),
        points: dashboard.value.back_catalog,
        previousPoints: previous.back_catalog,
        shareOf: 'sales'
      },
      {
        key: 'from_scheduled_cart',
        label: t(`${i18nScope}.charts.from_scheduled_cart`),
        points: dashboard.value.from_scheduled_cart,
        previousPoints: previous.from_scheduled_cart,
        shareOf: 'sales'
      },
      {
        key: 'not_from_scheduled_cart',
        label: t(`${i18nScope}.charts.not_from_scheduled_cart`),
        points: dashboard.value.not_from_scheduled_cart,
        previousPoints: previous.not_from_scheduled_cart,
        shareOf: 'sales'
      }
    ];
  });

  const salesTableColumns = computed(() => [t(`${i18nScope}.period`), ...salesSeries.value.map((entry) => entry.label)]);

  // Both subsets are parts of the sales, so the share is what says whether a
  // series is a sliver or most of the chart. Formatted, one decimal place.
  // `base` is the totals of one period, the current or the previous one, and
  // `key` the count among them, so a count is always set against the sales of
  // its own period. Missing totals read as no share.
  function shareOfSales(base, key) {
    if (!base || base.sales_count === 0) return formatPercent(0);
    return formatPercent((base[key] ?? 0) / base.sales_count);
  }

  const repurchaseShare = computed(() => shareOfSales(totals.value, 'repurchases_count'));
  const backCatalogShare = computed(() => shareOfSales(totals.value, 'back_catalog_count'));
  const fromScheduledCartShare = computed(() => shareOfSales(totals.value, 'from_scheduled_cart_count'));

  const salesSummary = computed(() => {
    if (!dashboard.value) return null;

    const sales = summaryFor('sales_count', formatCount(totals.value.sales_count), dashboard.value.sales);
    const repurchases = t(`${i18nScope}.repurchase_summary`, {
      total: formatCount(totals.value.repurchases_count),
      percent: repurchaseShare.value
    });
    const backCatalog = t(`${i18nScope}.back_catalog_summary`, {
      total: formatCount(totals.value.back_catalog_count),
      percent: backCatalogShare.value
    });
    const fromScheduledCart = t(`${i18nScope}.from_scheduled_cart_summary`, {
      total: formatCount(totals.value.from_scheduled_cart_count),
      percent: fromScheduledCartShare.value
    });

    return `${sales} ${repurchases} ${backCatalog} ${fromScheduledCart}`;
  });

  // The carts chart carries six series on the one count axis: the carts
  // created, the carts first validated, and two partitions of the validated
  // carts -- by whether the cart was created from a scheduled cart, and by
  // whether it is a customer-service cart. Each half of a partition declares
  // `shareOf: 'validated'`, so a period's readout carries the share of that
  // period's validated carts it makes up. The created carts are no base for
  // the validated ones: a cart is created in one period and validated in
  // another, so the two are not nested period by period.
  //
  // The series come in the order the server lists them, which is also the
  // order of the table columns and of the legend.
  const CART_SUBSETS = [
    'validated_from_scheduled_cart',
    'validated_not_from_scheduled_cart',
    'validated_customer_service',
    'validated_not_customer_service'
  ];

  const cartsSeries = computed(() => {
    if (!dashboard.value) return [];
    const previous = previousPeriod.value;

    return Object.keys(dashboard.value.carts).map((key) => ({
      key: key,
      label: t(`${i18nScope}.charts.carts_${key}`),
      points: dashboard.value.carts[key],
      previousPoints: previous.carts[key],
      ...(CART_SUBSETS.includes(key) ? { shareOf: 'validated' } : {})
    }));
  });

  const cartsTableColumns = computed(() => [t(`${i18nScope}.period`), ...cartsSeries.value.map((entry) => entry.label)]);

  // Only a user who can pick a provider needs to know how a cart mixing
  // providers is counted under one.
  const cartsHelp = computed(() => {
    const help = t(`${i18nScope}.help.carts`);
    return canFilterByProvider.value ? `${help} ${t(`${i18nScope}.help.carts_provider`)}` : help;
  });

  const cartsSummary = computed(() => {
    if (!dashboard.value) return null;
    const created = dashboard.value.carts.created;
    if (created.length === 0) return null;

    return t(`${i18nScope}.carts_summary`, {
      created: formatCount(totals.value.carts.created),
      validated: formatCount(totals.value.carts.validated),
      periods: created.length,
      start: formatBucket(created[0].bucket),
      end: formatBucket(created[created.length - 1].bucket)
    });
  });

  // One entry per currency rather than one joined string: revenue is never
  // converted, so several currencies on one line read as a single figure.
  function revenueLines(entries) {
    return entries.map((entry) => ({
      key: entry.currency,
      label: `${formatAmount(entry.total)} ${entry.currency.toUpperCase()}`
    }));
  }

  const revenueTotals = computed(() => {
    if (!totals.value || totals.value.revenue.length === 0) {
      return [{ key: 'none', label: formatAmount(0) }];
    }

    return revenueLines(totals.value.revenue);
  });

  // A single currency keeps the type scale of the sibling tiles. Several
  // stacked lines at that size make this tile, and so the whole row, markedly
  // taller than it needs to be, so the figures step down one size instead.
  const revenueTypeClass = computed(() => (revenueTotals.value.length > 1 ? 'h5' : 'h4'));

  // Absent for a reader the service did not work it out for.
  const uniqueBooks = computed(() => dashboard.value?.unique_books);

  // The books bought make up a sliver of the catalog, so the share carries
  // two decimals, and one too small to show reads as under the smallest one
  // shown rather than as none at all.
  const SMALLEST_CATALOG_SHARE = 0.0001;

  function catalogShareOf(books) {
    const share = books && books.catalog_count > 0 ? books.count / books.catalog_count : 0;
    if (share > 0 && share < SMALLEST_CATALOG_SHARE) return `< ${formatPercent(SMALLEST_CATALOG_SHARE, 2)}`;

    return formatPercent(share, 2);
  }

  const catalogShare = computed(() => catalogShareOf(uniqueBooks.value));

  // The summary figures over the previous period, the same range the charts
  // compare against, each shown under its tile's own figure.
  const previousTotals = computed(() => previousPeriod.value?.totals);
  const previousUniqueBooks = computed(() => previousPeriod.value?.unique_books);
  const previousRepurchaseShare = computed(() => shareOfSales(previousTotals.value, 'repurchases_count'));
  const previousBackCatalogShare = computed(() => shareOfSales(previousTotals.value, 'back_catalog_count'));
  // Only the currencies actually paid in: a zero amount under the current
  // figures would not say which currency it stands for. With none at all, the
  // tile shows no previous period.
  const previousRevenueTotals = computed(() =>
    revenueLines((previousTotals.value?.revenue ?? []).filter((entry) => entry.total !== 0))
  );
  const previousCatalogShare = computed(() => catalogShareOf(previousUniqueBooks.value));

  const previousRangeNotice = computed(() => {
    const range = previousPeriod.value?.date_range;
    if (!range) return null;

    return t(`${i18nScope}.totals.previous_period_range`, {
      start: formatBucket(range.start_date, 'day'),
      end: formatBucket(range.end_date, 'day')
    });
  });

  // Distinct hues rather than a single ramp: these categories are unordered,
  // so a sequential palette would imply a progression that is not there.
  const STATUS_COLOURS = {
    ready: '#0d6efd',
    active: '#198754',
    returned: '#6f42c1',
    expired: '#fd7e14',
    revoked: '#dc3545',
    cancelled: '#6c757d'
  };

  const statusEntries = computed(() => (dashboard.value?.loan_statuses ?? []).map((entry) => ({
    key: entry.status,
    label: t(`${i18nScope}.loan_statuses.${entry.status}`),
    count: entry.count,
    colour: STATUS_COLOURS[entry.status] || '#adb5bd'
  })));

  const statusRows = computed(() => {
    if (!dashboard.value) return [];
    const total = sumCounts(dashboard.value.loan_statuses);

    return dashboard.value.loan_statuses.map((entry) => [
      t(`${i18nScope}.loan_statuses.${entry.status}`),
      formatCount(entry.count),
      formatShare(entry.count, total)
    ]);
  });

  const statusSummary = computed(() => {
    if (!dashboard.value) return null;

    return breakdownSummary(statusEntries.value.filter((entry) => entry.count > 0));
  });

  // License types and providers are open-ended, so they take a hue by rank:
  // the most bought ones get their own slice and the long tail folds into one
  // neutral "Other" slice. Five hues keep neighbouring slices apart for
  // colour-blind readers.
  const RANKED_COLOURS = ['#0d6efd', '#fd7e14', '#198754', '#d63384', '#6f42c1'];
  const OTHER_COLOUR = '#adb5bd';

  // `entries` come ranked, biggest first; `otherLabel` names the folded tail
  // from how many entries it holds.
  function rankedSlices(entries, otherLabel) {
    const shown = entries.slice(0, RANKED_COLOURS.length)
      .map((entry, index) => ({ ...entry, colour: RANKED_COLOURS[index] }));
    const rest = entries.slice(RANKED_COLOURS.length);
    if (rest.length === 0) return shown;

    return [
      ...shown,
      {
        key: 'other',
        label: otherLabel(rest.length),
        count: sumCounts(rest),
        colour: OTHER_COLOUR
      }
    ];
  }
  const UNLIMITED = 'unlimited';

  const licenseTypes = computed(() => dashboard.value?.license_types ?? []);

  function licenseTokensLabel(tokens) {
    return tokens === UNLIMITED
      ? t(`${i18nScope}.license_types.unlimited_tokens`)
      : t(`${i18nScope}.license_types.tokens`, Number(tokens));
  }

  function licenseConcurrentUsersLabel(concurrentUsers) {
    return concurrentUsers === UNLIMITED
      ? t(`${i18nScope}.license_types.unlimited_concurrent_users`)
      : t(`${i18nScope}.license_types.concurrent_users`, Number(concurrentUsers));
  }

  // Lifetimes are stored in days; a whole number of years reads as years.
  function licenseLifetimeLabel(lifetime) {
    if (lifetime === UNLIMITED) return t(`${i18nScope}.license_types.unlimited_lifetime`);

    const days = Number(lifetime);
    return days % 365 === 0
      ? t(`${i18nScope}.license_types.lifetime_years`, days / 365)
      : t(`${i18nScope}.license_types.lifetime_days`, days);
  }

  function licenseTypeLabel(entry) {
    return [
      licenseTokensLabel(entry.tokens),
      licenseConcurrentUsersLabel(entry.concurrent_users),
      licenseLifetimeLabel(entry.lifetime)
    ].join(' · ');
  }

  // The service ranks the license types, most bought first.
  const licenseTypeEntries = computed(() => rankedSlices(
    licenseTypes.value.map((entry) => ({
      key: [entry.tokens, entry.concurrent_users, entry.lifetime].join('|'),
      label: licenseTypeLabel(entry),
      count: entry.count
    })),
    (count) => t(`${i18nScope}.license_types.other`, count)
  ));

  // The table lists every license type, the tail the chart folds away included.
  const licenseTypeRows = computed(() => {
    const total = sumCounts(licenseTypes.value);

    return licenseTypes.value.map((entry) => [
      licenseTokensLabel(entry.tokens),
      licenseConcurrentUsersLabel(entry.concurrent_users),
      licenseLifetimeLabel(entry.lifetime),
      formatCount(entry.count),
      formatShare(entry.count, total)
    ]);
  });

  const licenseTypeSummary = computed(() => breakdownSummary(licenseTypeEntries.value));

  // Absent when the dashboard is narrowed to one provider, the breakdown
  // being that provider alone, and for anyone but an internal user. The
  // service ranks them, most bought first.
  const providerBreakdown = computed(() => dashboard.value?.providers ?? []);

  // Copies with no provider come back under a null one, so the breakdown
  // still adds up to the sales count.
  function providerLabel(entry) {
    return entry.provider ?? t(`${i18nScope}.unknown_provider`);
  }

  const providerEntries = computed(() => rankedSlices(
    providerBreakdown.value.map((entry) => ({ key: entry.provider ?? '', label: providerLabel(entry), count: entry.count })),
    (count) => t(`${i18nScope}.other_providers`, count)
  ));

  // The table lists every provider, the tail the chart folds away included.
  const providerRows = computed(() => {
    const total = sumCounts(providerBreakdown.value);

    return providerBreakdown.value.map((entry) => [providerLabel(entry), formatCount(entry.count), formatShare(entry.count, total)]);
  });

  const providerSummary = computed(() => breakdownSummary(providerEntries.value));

  const topCarousels = computed(() => dashboard.value?.top_carousels ?? []);

  function carouselLabel(entry) {
    return entry.title || t(`${i18nScope}.deleted_carousel`, { id: entry.carousel_id });
  }

  const carouselRows = computed(() => topCarousels.value.map((entry) => [carouselLabel(entry), formatCount(entry.sales)]));

  const carouselSummary = computed(() => {
    if (topCarousels.value.length === 0) return t(`${i18nScope}.no_data`);

    return topCarousels.value.map((entry) => `${carouselLabel(entry)}: ${formatCount(entry.sales)}`).join('. ');
  });

  // Sections the user folded away, kept per browser across reloads. The
  // revenue charts, one per currency, fold together under a single key.
  const { isCollapsed, toggle: toggleSection, expandAll, anyCollapsed } = useCollapsedSections('sales_dashboard.collapsed_sections');
  const summarySectionId = useId();

  onMounted(fetchData);
</script>

<template>
  <div class="sales-dashboard">
    <!-- Filters -->
    <form class="dashboard-filters card card-body mb-4" @submit.prevent="fetchData">
      <fieldset>
        <legend class="h6">{{ t(`${i18nScope}.filters`) }}</legend>

        <div class="row g-3 align-items-end">
          <div class="col-12 col-sm-6 col-lg-auto">
            <label class="form-label" for="dashboard-start-date">{{ t(`${i18nScope}.start_date`) }}</label>
            <input id="dashboard-start-date" v-model="startDate" type="date" class="form-control" />
          </div>

          <div class="col-12 col-sm-6 col-lg-auto">
            <label class="form-label" for="dashboard-end-date">{{ t(`${i18nScope}.end_date`) }}</label>
            <input id="dashboard-end-date" v-model="endDate" type="date" class="form-control" />
          </div>

          <div class="col-12 col-sm-6 col-lg-auto">
            <label class="form-label" for="dashboard-granularity">{{ t(`${i18nScope}.granularity`) }}</label>
            <select id="dashboard-granularity" v-model="granularity" class="form-select">
              <option v-for="option in granularities" :key="option" :value="option">
                {{ t(`${i18nScope}.granularities.${option}`) }}
              </option>
            </select>
          </div>

          <div v-if="canFilterByNetwork" class="col-12 col-sm-6 col-lg-auto">
            <label class="form-label" for="dashboard-network">{{ t(`${i18nScope}.network`) }}</label>
            <select id="dashboard-network" v-model="networkId" class="form-select">
              <option value="">{{ t(`${i18nScope}.all_networks`) }}</option>
              <option v-for="network in networks" :key="network.id" :value="network.id">{{ network.name }}</option>
            </select>
          </div>

          <div v-if="canFilterByCountry" class="col-12 col-sm-6 col-lg-auto">
            <label class="form-label" for="dashboard-country">{{ t(`${i18nScope}.country`) }}</label>
            <select id="dashboard-country" v-model="countryId" class="form-select">
              <option value="">{{ t(`${i18nScope}.all_countries`) }}</option>
              <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
            </select>
          </div>

          <div v-if="canFilterByLibrary" class="col-12 col-sm-6 col-lg-auto">
            <label class="form-label" for="dashboard-library">{{ t(`${i18nScope}.library`) }}</label>
            <select id="dashboard-library" v-model="libraryId" class="form-select">
              <option value="">{{ t(`${i18nScope}.all_libraries`) }}</option>
              <option v-for="library in availableLibraries" :key="library.id" :value="library.id">
                {{ library.name }}
              </option>
            </select>
          </div>

          <div v-if="canFilterByProvider" class="col-12 col-sm-6 col-lg-auto">
            <label class="form-label" for="dashboard-provider">{{ t(`${i18nScope}.provider`) }}</label>
            <select id="dashboard-provider" v-model="provider" class="form-select">
              <option value="">{{ t(`${i18nScope}.all_providers`) }}</option>
              <option v-for="option in providers" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>

          <div class="col-12 col-lg-auto d-flex gap-2">
            <button type="submit" class="btn btn-primary" :disabled="!canQuery">
              {{ t(`${i18nScope}.refresh`) }}
            </button>
            <a
              class="btn btn-outline-secondary"
              :class="{ disabled: !canQuery || loading || !dashboard }"
              :href="exportHref"
              :aria-disabled="!canQuery || loading || !dashboard"
            >
              <i class="bi bi-download me-1" aria-hidden="true"></i>{{ t(`${i18nScope}.download_csv`) }}
            </a>
          </div>
        </div>
      </fieldset>
    </form>

    <!-- Says plainly whose data is on screen, whatever the filters are set to -->
    <p class="alert alert-info" role="note">{{ scopeNotice }}</p>

    <!-- Client-side guards, mirroring what the server enforces -->
    <div v-if="rangeInverted" class="alert alert-warning" role="alert">
      {{ t(`${i18nScope}.invalid_range`) }}
    </div>
    <div v-else-if="tooManyBuckets" class="alert alert-warning" role="alert">
      {{ t(`${i18nScope}.too_many_buckets`, { buckets: projectedBuckets, max: maxBuckets }) }}
    </div>

    <div v-if="canQuery" aria-live="polite" aria-atomic="true">
      <div v-if="loading" class="text-center py-5">
        <Loader />
        <p class="mt-3">{{ t(`${i18nScope}.loading`) }}</p>
      </div>

      <div v-else-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

      <template v-else-if="dashboard">
        <div v-if="anyCollapsed" class="text-end mb-3">
          <button type="button" class="btn btn-link btn-sm p-0" @click="expandAll">{{ t(`${i18nScope}.show_all_sections`) }}</button>
        </div>

        <!-- Summary tiles -->
        <section class="mb-4" :aria-label="t(`${i18nScope}.summary_title`)">
          <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
            <h2 class="h5 mb-0">{{ t(`${i18nScope}.summary_title`) }}</h2>
            <SectionToggle
              :collapsed="isCollapsed('summary')"
              :section="t(`${i18nScope}.summary_title`)"
              :controls="summarySectionId"
              @toggle="toggleSection('summary')"
            />
          </div>
          <div v-if="!isCollapsed('summary')" :id="summarySectionId">
            <p v-if="previousRangeNotice" class="text-muted small mb-2">{{ previousRangeNotice }}</p>
            <div class="row g-3">
              <div class="col-12 col-md-6 col-xl">
                <div class="card h-100">
                  <div class="card-body">
                    <p class="text-muted mb-1 small">{{ t(`${i18nScope}.totals.sales_count`) }}</p>
                    <p class="h4 mb-0">{{ formatCount(totals.sales_count) }}</p>
                    <p class="text-muted mb-0 small">
                      {{ t(`${i18nScope}.totals.repurchases_share`, { total: formatCount(totals.repurchases_count), percent: repurchaseShare }) }}
                    </p>
                    <p class="text-muted mb-0 small">
                      {{ t(`${i18nScope}.totals.back_catalog_share`, { total: formatCount(totals.back_catalog_count), percent: backCatalogShare }) }}
                    </p>
                    <PreviousPeriodFigure
                      v-if="previousTotals"
                      :figure="formatCount(previousTotals.sales_count)"
                      :details="[
                        t(`${i18nScope}.totals.repurchases_share`, { total: formatCount(previousTotals.repurchases_count), percent: previousRepurchaseShare }),
                        t(`${i18nScope}.totals.back_catalog_share`, { total: formatCount(previousTotals.back_catalog_count), percent: previousBackCatalogShare })
                      ]"
                    />
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6 col-xl">
                <div class="card h-100">
                  <div class="card-body">
                    <p class="text-muted mb-1 small d-flex align-items-center gap-1">
                      {{ t(`${i18nScope}.totals.revenue`) }}
                      <button
                        type="button"
                        class="btn btn-link p-0 border-0 help-trigger"
                        data-bs-toggle="tooltip"
                        :data-bs-title="t(`${i18nScope}.totals.revenue_help`)"
                        :aria-label="t(`${i18nScope}.totals.revenue_help`)"
                      >
                        <i class="bi bi-info-circle" aria-hidden="true"></i>
                      </button>
                    </p>
                    <ul class="mb-0 ps-4" :class="revenueTypeClass">
                      <li v-for="entry in revenueTotals" :key="entry.key">{{ entry.label }}</li>
                    </ul>
                    <PreviousPeriodFigure v-if="previousRevenueTotals.length > 0">
                      <ul class="mb-0 ps-4 h6">
                        <li v-for="entry in previousRevenueTotals" :key="entry.key">{{ entry.label }}</li>
                      </ul>
                    </PreviousPeriodFigure>
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6 col-xl">
                <div class="card h-100">
                  <div class="card-body">
                    <p class="text-muted mb-1 small">{{ t(`${i18nScope}.totals.loans_count`) }}</p>
                    <p class="h4 mb-0">{{ formatCount(totals.loans_count) }}</p>
                    <PreviousPeriodFigure v-if="previousTotals" :figure="formatCount(previousTotals.loans_count)" />
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6 col-xl">
                <div class="card h-100">
                  <div class="card-body">
                    <p class="text-muted mb-1 small">{{ t(`${i18nScope}.totals.libraries`) }}</p>
                    <p class="h4 mb-0">{{ formatCount(dashboard.library_count) }}</p>
                    <PreviousPeriodFigure
                      v-if="previousPeriod?.library_count != null"
                      :figure="formatCount(previousPeriod.library_count)"
                    />
                  </div>
                </div>
              </div>
              <div v-if="uniqueBooks" class="col-12 col-md-6 col-xl">
                <div class="card h-100">
                  <div class="card-body">
                    <p class="text-muted mb-1 small d-flex align-items-center gap-1">
                      {{ t(`${i18nScope}.totals.unique_books`) }}
                      <button
                        type="button"
                        class="btn btn-link p-0 border-0 help-trigger"
                        data-bs-toggle="tooltip"
                        :data-bs-title="t(`${i18nScope}.totals.unique_books_help`)"
                        :aria-label="t(`${i18nScope}.totals.unique_books_help`)"
                      >
                        <i class="bi bi-info-circle" aria-hidden="true"></i>
                      </button>
                    </p>
                    <p class="h4 mb-0">{{ formatCount(uniqueBooks.count) }}</p>
                    <p class="text-muted mb-0 small">
                      {{ t(`${i18nScope}.totals.unique_books_share`, { percent: catalogShare, catalog: formatCount(uniqueBooks.catalog_count) }) }}
                    </p>
                    <PreviousPeriodFigure
                      v-if="previousUniqueBooks"
                      :figure="formatCount(previousUniqueBooks.count)"
                      :details="[t(`${i18nScope}.totals.unique_books_previous_share`, { percent: previousCatalogShare })]"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Sales count -->
        <ChartCard
          :title="t(`${i18nScope}.charts.sales_count`)"
          :help="t(`${i18nScope}.help.sales_count`)"
          :collapsed="isCollapsed('sales_count')"
          @toggle="toggleSection('sales_count')"
          :summary="salesSummary"
          :table-columns="salesTableColumns"
          :table-rows="multiSeriesRows(salesSeries, formatCount)"
          :table-caption="t(`${i18nScope}.data_table_caption`, { chart: t(`${i18nScope}.charts.sales_count`) })"
        >
          <TimeSeriesChart :series="salesSeries" :granularity="dashboard.granularity" storage-key="sales_dashboard.chart.sales_count" />
        </ChartCard>

        <!-- Carts: created per period, and first validated per period split by
             origin and by kind -->
        <ChartCard
          :title="t(`${i18nScope}.charts.carts`)"
          :help="cartsHelp"
          :collapsed="isCollapsed('carts')"
          @toggle="toggleSection('carts')"
          :summary="cartsSummary"
          :table-columns="cartsTableColumns"
          :table-rows="multiSeriesRows(cartsSeries, formatCount)"
          :table-caption="t(`${i18nScope}.data_table_caption`, { chart: t(`${i18nScope}.charts.carts`) })"
        >
          <TimeSeriesChart :series="cartsSeries" :granularity="dashboard.granularity" storage-key="sales_dashboard.chart.carts" />
        </ChartCard>

        <!-- Revenue, one chart per currency so nothing is silently converted -->
        <ChartCard
          v-for="entry in dashboard.revenue"
          :key="entry.currency"
          :title="t(`${i18nScope}.revenue_in`, { currency: entry.currency.toUpperCase() })"
          :help="t(`${i18nScope}.help.revenue`)"
          :collapsed="isCollapsed('revenue')"
          @toggle="toggleSection('revenue')"
          :summary="revenueSummary(entry)"
          :table-columns="revenueTableColumns(entry)"
          :table-rows="multiSeriesRows(revenueSeries(entry), formatAmount)"
          :table-caption="t(`${i18nScope}.data_table_caption`, { chart: t(`${i18nScope}.charts.revenue`) })"
        >
          <TimeSeriesChart
            :series="revenueSeries(entry)"
            :granularity="dashboard.granularity"
            format="currency"
            :currency="entry.currency"
            :storage-key="`sales_dashboard.chart.revenue.${entry.currency}`"
          />
        </ChartCard>

        <!-- Revenue with no rows at all still needs to say so -->
        <ChartCard
          v-if="dashboard.revenue.length === 0"
          :title="t(`${i18nScope}.charts.revenue`)"
          :help="t(`${i18nScope}.help.revenue`)"
          :collapsed="isCollapsed('revenue')"
          @toggle="toggleSection('revenue')"
        >
          <p class="text-center text-muted py-4 mb-0">{{ t(`${i18nScope}.no_data`) }}</p>
        </ChartCard>

        <!-- Loans -->
        <ChartCard
          :title="t(`${i18nScope}.charts.loans_count`)"
          :help="t(`${i18nScope}.help.loans_count`)"
          :collapsed="isCollapsed('loans_count')"
          @toggle="toggleSection('loans_count')"
          :summary="summaryFor('loans_count', formatCount(totals.loans_count), dashboard.loans)"
          :table-columns="[t(`${i18nScope}.period`), t(`${i18nScope}.charts.loans_count`)]"
          :table-rows="seriesRows(dashboard.loans, formatCount)"
          :table-caption="t(`${i18nScope}.data_table_caption`, { chart: t(`${i18nScope}.charts.loans_count`) })"
        >
          <TimeSeriesChart
            :points="dashboard.loans"
            :previous-points="previousPeriod.loans"
            :granularity="dashboard.granularity"
            storage-key="sales_dashboard.chart.loans_count"
          />
        </ChartCard>

        <!-- Most popular carousels -->
        <ChartCard
          :title="t(`${i18nScope}.charts.top_carousels`)"
          :help="t(`${i18nScope}.help.top_carousels`)"
          :collapsed="isCollapsed('top_carousels')"
          @toggle="toggleSection('top_carousels')"
          :summary="carouselSummary"
          :table-columns="[t(`${i18nScope}.carousel`), t(`${i18nScope}.charts.sales_count`)]"
          :table-rows="carouselRows"
          :table-caption="t(`${i18nScope}.carousel_table_caption`)"
        >
          <TopCarouselsChart :carousels="topCarousels" />
        </ChartCard>

        <!-- Loan statuses -->
        <ChartCard
          :title="t(`${i18nScope}.charts.loan_statuses`)"
          :help="t(`${i18nScope}.help.loan_statuses`)"
          :collapsed="isCollapsed('loan_statuses')"
          @toggle="toggleSection('loan_statuses')"
          :summary="statusSummary"
          :table-columns="[t(`${i18nScope}.charts.loan_statuses`), t(`${i18nScope}.charts.loans_count`), t(`${i18nScope}.value`)]"
          :table-rows="statusRows"
          :table-caption="t(`${i18nScope}.status_table_caption`)"
        >
          <DonutChart :entries="statusEntries" :total-label="t(`${i18nScope}.total_loans_label`)" />
        </ChartCard>

        <!-- License types -->
        <ChartCard
          :title="t(`${i18nScope}.charts.license_types`)"
          :help="t(`${i18nScope}.help.license_types`)"
          :collapsed="isCollapsed('license_types')"
          @toggle="toggleSection('license_types')"
          :summary="licenseTypeSummary"
          :table-columns="[
            t(`${i18nScope}.license_types.tokens_column`),
            t(`${i18nScope}.license_types.concurrent_users_column`),
            t(`${i18nScope}.license_types.lifetime_column`),
            t(`${i18nScope}.license_types.copies_column`),
            t(`${i18nScope}.value`)
          ]"
          :table-rows="licenseTypeRows"
          :table-caption="t(`${i18nScope}.license_type_table_caption`)"
        >
          <DonutChart :entries="licenseTypeEntries" :total-label="t(`${i18nScope}.total_copies_label`)" />
        </ChartCard>

        <!-- Providers, for internal users and only across every provider:
             narrowed to one, the breakdown would be that provider alone -->
        <ChartCard
          v-if="dashboard.providers"
          :title="t(`${i18nScope}.charts.providers`)"
          :help="t(`${i18nScope}.help.providers`)"
          :collapsed="isCollapsed('providers')"
          @toggle="toggleSection('providers')"
          :summary="providerSummary"
          :table-columns="[t(`${i18nScope}.provider`), t(`${i18nScope}.license_types.copies_column`), t(`${i18nScope}.value`)]"
          :table-rows="providerRows"
          :table-caption="t(`${i18nScope}.provider_table_caption`)"
        >
          <DonutChart :entries="providerEntries" :total-label="t(`${i18nScope}.total_copies_label`)" />
        </ChartCard>
      </template>
    </div>
  </div>
</template>

<style scoped>
  .sales-dashboard {
    padding: 20px 0;
  }

  .dashboard-filters legend {
    float: none;
    margin-bottom: 0.75rem;
  }

  .help-trigger {
    color: #6c757d;
    line-height: 1;
  }

  .help-trigger:hover,
  .help-trigger:focus {
    color: #0d6efd;
  }
</style>

<i18n src="../../locales/en.json" global></i18n>
<i18n src="../../locales/fr.json" global></i18n>
<i18n src="../../locales/es.json" global></i18n>
<i18n src="../../locales/it.json" global></i18n>
<i18n src="../../locales/pt.json" global></i18n>
